/**
 * Auth store (zustand), shared by both apps. Wraps Supabase Auth + the
 * analyzer profile (role). Source of truth for "who is logged in".
 */
import { create } from 'zustand'
import { supabase, SUPABASE_CONFIGURED } from './supabase'
import { apiFetch } from './api'
import type { Profile, UserRole } from './profiles'

interface AuthState {
  profile: Profile | null
  role: UserRole
  isAuthenticated: boolean
  isAdmin: boolean
  isStaff: boolean
  loading: boolean
  initialized: boolean
  login: (email: string, password: string) => Promise<{ error: string | null }>
  loginWithGoogle: () => Promise<{ error: string | null }>
  loginWithLinkedIn: () => Promise<{ error: string | null }>
  register: (email: string, password: string, fullName: string) => Promise<{ error: string | null }>
  logout: () => Promise<void>
  initialize: () => Promise<void>
  refreshProfile: () => Promise<void>
}

function setRole(set: (s: Partial<AuthState>) => void, profile: Profile | null) {
  const role = (profile?.role as UserRole) || 'user'
  set({
    profile,
    role,
    isAuthenticated: true,
    isAdmin: role === 'admin',
    isStaff: role === 'admin' || role === 'backoffice',
    loading: false,
    initialized: true,
  })
}

function clearAuth(set: (s: Partial<AuthState>) => void) {
  set({
    profile: null,
    role: 'user',
    isAuthenticated: false,
    isAdmin: false,
    isStaff: false,
    loading: false,
    initialized: true,
  })
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  profile: null,
  role: 'user' as UserRole,
  isAuthenticated: false,
  isAdmin: false,
  isStaff: false,
  loading: true,
  initialized: false,

  initialize: async () => {
    if (get().initialized) return

    if (!SUPABASE_CONFIGURED) {
      clearAuth(set)
      return
    }

    try {
      // Check existing Supabase session
      const { data: { session } } = await supabase.auth.getSession()

      if (session) {
        const { data: profile } = await apiFetch<Profile>('/profiles/me')
        setRole(set, profile)
      } else {
        clearAuth(set)
      }

      // Listen for auth state changes (token refresh, OAuth callback, logout)
      supabase.auth.onAuthStateChange(async (event, session) => {
        if (event === 'SIGNED_IN' && session) {
          const { data: profile } = await apiFetch<Profile>('/profiles/me')
          setRole(set, profile)
        } else if (event === 'SIGNED_OUT') {
          clearAuth(set)
        } else if (event === 'TOKEN_REFRESHED' && session) {
          // Token refreshed, profile stays the same
        }
      })
    } catch (err) {
      console.warn('[auth] initialize failed:', err)
      clearAuth(set)
    }
  },

  refreshProfile: async () => {
    const { data: profile } = await apiFetch<Profile>('/profiles/me')
    if (!profile) return
    const role = (profile.role as UserRole) || 'user'
    set({
      profile,
      role,
      isAdmin: role === 'admin',
      isStaff: role === 'admin' || role === 'backoffice',
    })
  },

  login: async (email: string, password: string) => {
    set({ loading: true })
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      set({ loading: false })
      return { error: error.message }
    }
    // onAuthStateChange handles the rest
    return { error: null }
  },

  loginWithGoogle: async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    })
    if (error) return { error: error.message }
    return { error: null }
  },

  loginWithLinkedIn: async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'linkedin_oidc',
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    })
    if (error) return { error: error.message }
    return { error: null }
  },

  register: async (email: string, password: string, fullName: string) => {
    set({ loading: true })
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName } },
    })
    if (error) {
      set({ loading: false })
      return { error: error.message }
    }
    return { error: null }
  },

  logout: async () => {
    await supabase.auth.signOut()
    clearAuth(set)
  },
}))
